import { persistDisplaySettingsCookie } from './displaySettingsCookie'
import { domainURL, domainLanguages } from './domainURL'
import { Rates } from 'services/pay/models'
import sendTrackerLog from './tracker'

// Types for better type safety
export interface DisplaySettings {
  country: string
  language: string
  currency: string
}

export interface CurrentState {
  market: string
  language: string
  currency: string
}

export interface ChangeFlags {
  hasMarketChanged: boolean
  hasLanguageChanged: boolean
  hasCurrencyChanged: boolean
}

export interface DisplaySettingsContext {
  setCurrency?: (currency: { iso: string; conversion: number }) => void
  currencyConversions?: Rates
}

// Pure function to detect what changed
export const detectChanges = (settings: DisplaySettings, current: CurrentState): ChangeFlags => {
  return {
    hasMarketChanged: settings.country !== current.market,
    hasLanguageChanged: settings.language !== current.language,
    hasCurrencyChanged: settings.currency !== current.currency,
  }
}

// Pure function to clean URL from speed expansions
export const cleanUrlFromExpansions = (url: string): string => {
  // Remove all speed expansions: /us-ro/de-en/flights/... → /flights/...
  return url.replace(/^\/[a-z]{2}(-[a-z]{2})?(\/[a-z]{2}(-[a-z]{2})?)*/, '')
}

// Pure function to build new locale URL (updated for single language format)
export const buildLocaleUrl = (market: string, language: string, pathname: string, search: string): string => {
  const cleanPath = cleanUrlFromExpansions(pathname)
  const targetDomainDefaultLanguage = domainLanguages[market]

  // Use single language format instead of market-language format
  if (language === targetDomainDefaultLanguage) {
    // Default language: clean path without expansion
    return `${cleanPath}${search}`
  } else {
    // Non-default language: use single language format
    return `/${language}${cleanPath}${search}`
  }
}

// Pure function to build domain URL for market changes (updated for single language format)
export const buildDomainUrl = (settings: DisplaySettings, pathname: string, search: string): string => {
  const newDomainUrl = domainURL[settings.country] || globalThis.window.location.origin
  const targetDomainDefaultLanguage = domainLanguages[settings.country]
  const isDefaultLanguage = settings.language === targetDomainDefaultLanguage

  let targetUrl: string

  if (settings.country != 'tr') {
    pathname = pathname.replace(/\/ucak-bileti\//, '')
  }

  if (isDefaultLanguage) {
    // Default language: clean path without expansion
    targetUrl = cleanUrlFromExpansions(pathname) + search
    // Handle root path edge case
    if (!targetUrl || targetUrl === '/') {
      targetUrl = search || ''
    }
  } else {
    // Speed expansion required - use single language format instead of market-language
    const cleanPath = cleanUrlFromExpansions(pathname)
    targetUrl = `/${settings.language}${cleanPath}${search}`
  }

  return `${newDomainUrl}${targetUrl}`
}

// Side effect function to update cookies
export const updateDisplaySettingsCookies = (settings: DisplaySettings, currencyConversions?: Rates): void => {
  // Get conversion dynamically from currencyConversions, fallback to 1
  // Rates values are strings, so we need to parse them to numbers
  const conversionValue = currencyConversions?.[settings.currency as keyof Rates]
  const conversion = conversionValue ? Number.parseFloat(conversionValue) : 1
  const currencyValue = { iso: settings.currency, conversion }

  const cookieData = {
    domain: settings.country,
    language: settings.language,
    currency: currencyValue,
  }

  persistDisplaySettingsCookie(cookieData)
}

// Side effect function to update currency in app state
export const updateCurrencyState = (currency: string, setCurrency?: (currency: { iso: string; conversion: number }) => void, currencyConversions?: Rates): void => {
  if (setCurrency) {
    // Rates values are strings, so we need to parse them to numbers
    const conversionValue = currencyConversions?.[currency as keyof Rates]
    const conversion = conversionValue ? Number.parseFloat(conversionValue) : 1
    setCurrency({ iso: currency, conversion })
  }
}

// Side effect function to navigate to new URL
export const navigateToUrl = (url: string): void => {
  globalThis.window.location.href = url
}

// Strategy pattern for handling different change scenarios
export const handleCurrencyOnlyChange = (settings: DisplaySettings, context: DisplaySettingsContext, current: CurrentState): void => {
  // Track currency change event
  sendTrackerLog({
    category: 'display-settings',
    page: globalThis.window.location.pathname,
    type: 'currency-change',
    message: `Currency changed from ${current.currency} to ${settings.currency}`,
    from_currency: current.currency,
    to_currency: settings.currency,
    market: settings.country,
    language: settings.language,
  })

  updateCurrencyState(settings.currency, context.setCurrency, context.currencyConversions)
}

export const handleLanguageOnlyChange = (settings: DisplaySettings, current: CurrentState): void => {
  // Track language change event (may include currency change)
  const changeDetails: string[] = []
  const eventData: Record<string, string> = {
    category: 'display-settings',
    page: globalThis.window.location.pathname,
    type: 'language-change',
    from_language: current.language,
    to_language: settings.language,
    market: settings.country,
    from_currency: current.currency,
    to_currency: settings.currency,
  }

  changeDetails.push(`language: ${current.language} → ${settings.language}`)

  // Check if currency also changed
  if (current.currency !== settings.currency) {
    changeDetails.push(`currency: ${current.currency} → ${settings.currency}`)
    eventData.type = 'language-currency-change'
  }

  sendTrackerLog({
    ...eventData,
    message: changeDetails.length > 1 ? `Language and currency changed: ${changeDetails.join(', ')}` : `Language changed: ${changeDetails.join(', ')}`,
  })

  const newUrl = buildLocaleUrl(settings.country, settings.language, globalThis.window.location.pathname, globalThis.window.location.search)
  sendTrackerLog({
    category: 'display-settings',
    page: globalThis.window.location.pathname,
    type: 'navigation',
    message: `Navigating to: ${newUrl}`,
    target_url: newUrl,
  })

  navigateToUrl(newUrl)
}

export const handleMarketChange = (settings: DisplaySettings, current: CurrentState): void => {
  // Track market change event (may include language and currency changes)
  const changeDetails: string[] = []
  const eventData: Record<string, string> = {
    category: 'display-settings',
    page: globalThis.window.location.pathname,
    type: 'market-change',
    from_market: current.market,
    to_market: settings.country,
    from_language: current.language,
    to_language: settings.language,
    from_currency: current.currency,
    to_currency: settings.currency,
  }

  if (current.market !== settings.country) {
    changeDetails.push(`market: ${current.market} → ${settings.country}`)
  }
  if (current.language !== settings.language) {
    changeDetails.push(`language: ${current.language} → ${settings.language}`)
  }
  if (current.currency !== settings.currency) {
    changeDetails.push(`currency: ${current.currency} → ${settings.currency}`)
  }

  sendTrackerLog({
    ...eventData,
    message: `Market changed: ${changeDetails.join(', ')}`,
  })

  const newUrl = buildDomainUrl(settings, globalThis.window.location.pathname, globalThis.window.location.search)
  sendTrackerLog({
    category: 'display-settings',
    page: globalThis.window.location.pathname,
    type: 'navigation',
    message: `Navigating to: ${newUrl}`,
    target_url: newUrl,
    from_market: current.market,
    to_market: settings.country,
  })

  navigateToUrl(newUrl)
}

const ENUYGUN_TO_WINGIE_LANGUAGE_REDIRECT_MAP: Record<string, string> = {
  en: 'https://www.wingie.com/',
  ar: 'https://sa.wingie.com/',
  ru: 'https://ru.wingie.com/',
  es: 'https://www.wingie.es/',
  de: 'https://www.wingie.de/',
  fr: 'https://www.wingie.fr/',
  hi: 'https://www.wingie.com/hi/',
  it: 'https://www.wingie.it/',
  ja: 'https://www.wingie.com/ja/',
  ka: 'https://www.wingie.com/ka/',
  kk: 'https://www.wingie.com/kk/',
  ko: 'https://www.wingie.com/ko/',
  ky: 'https://www.wingie.com/ky/',
  lt: 'https://www.wingie.com/lt/',
  nl: 'https://www.wingie.com/nl/',
  pl: 'https://www.wingie.pl/',
  pt: 'https://www.wingie.com/pt/',
  ro: 'https://www.wingie.com/ro/',
  sr: 'https://www.wingie.com/sr/',
  ta: 'https://www.wingie.com/ta/',
  tg: 'https://www.wingie.com/tg/',
  th: 'https://www.wingie.com/th/',
  ur: 'https://www.wingie.com/ur/',
  uz: 'https://www.wingie.com/uz/',
  zh: 'https://www.wingie.com/zh/',
}

/** Redirect from Wingie to Enuygun when Turkey is selected. Returns true if redirect was handled. */
export const handleWingieToEnuygunRedirect = (settings: DisplaySettings, current: CurrentState, deviceType: string): boolean => {
  // Language does not matter - always redirect to enuygun.com
  if (current.market === 'tr' || settings.country !== 'tr') {
    return false
  }

  const enuygunBaseUrl = domainURL.tr || 'https://www.enuygun.com/'
  const cleanPath = cleanUrlFromExpansions(globalThis.window.location.pathname)
  const targetPath = cleanPath && cleanPath !== '/' ? cleanPath : ''
  const redirectUrl = `${enuygunBaseUrl}${targetPath}${globalThis.window.location.search || ''}`

  const startTime = Date.now()
  const browserLanguage = globalThis.navigator === undefined ? '' : globalThis.navigator.language
  const sourceUrl = globalThis.window === undefined ? '' : globalThis.window.location.href

  sendTrackerLog({
    category: 'display-settings',
    page: globalThis.window.location.pathname,
    type: 'language-change-redirect',
    message: `Redirecting from Wingie to Enuygun for Turkey (Country/Region)`,
    from_language: current.language,
    to_language: settings.language,
    from_currency: current.currency,
    to_currency: settings.currency,
    market: current.market,
    source: 'Wingie',
    target_url: redirectUrl,
    source_url: sourceUrl,
    browser_language: browserLanguage,
    device_type: deviceType,
    redirect_latency: Date.now() - startTime,
  })

  setTimeout(() => {
    navigateToUrl(redirectUrl)
  }, 300)
  return true
}

/** Redirect Enuygun → Wingie for unsupported languages. Returns true if redirect was handled. */
export const handleEnuygunToWingieLanguageRedirect = (settings: DisplaySettings, current: CurrentState, deviceType: string): boolean => {
  // When on Enuygun: these languages are not natively supported, redirect to Wingie domains
  if (current.market !== 'tr' || settings.country !== 'tr') {
    return false
  }

  const redirectUrl = ENUYGUN_TO_WINGIE_LANGUAGE_REDIRECT_MAP[settings.language]
  if (!redirectUrl) {
    return false
  }

  const startTime = Date.now()
  const browserLanguage = globalThis.navigator === undefined ? '' : globalThis.navigator.language
  const sourceUrl = globalThis.window === undefined ? '' : globalThis.window.location.href

  sendTrackerLog({
    category: 'display-settings',
    page: globalThis.window.location.pathname,
    type: 'language-change-redirect',
    message: `Redirecting from Enuygun to Wingie domain for language: ${settings.language}`,
    from_language: current.language,
    to_language: settings.language,
    from_currency: current.currency,
    to_currency: settings.currency,
    market: settings.country,
    source: 'Enuygun',
    target_url: redirectUrl,
    source_url: sourceUrl,
    browser_language: browserLanguage,
    device_type: deviceType,
    redirect_latency: Date.now() - startTime,
  })

  // Add a small delay to ensure log request is dispatched before navigation
  // This is especially helpful for checking network logs in dev tools
  setTimeout(() => {
    navigateToUrl(redirectUrl)
  }, 300)
  return true
}

// Main handler function following single responsibility principle
export const handleDisplaySettingsChange = (settings: DisplaySettings, current: CurrentState, context: DisplaySettingsContext, deviceType = 'unknown'): void => {
  if (handleWingieToEnuygunRedirect(settings, current, deviceType)) {
    return
  }

  if (handleEnuygunToWingieLanguageRedirect(settings, current, deviceType)) {
    return
  }

  // Always update cookies first with dynamic conversion
  updateDisplaySettingsCookies(settings, context.currencyConversions)

  // Detect what changed
  const changes = detectChanges(settings, current)

  // Always update currency state if changed (no navigation needed)
  if (changes.hasCurrencyChanged) {
    updateCurrencyState(settings.currency, context.setCurrency, context.currencyConversions)
  }

  // Early return for currency-only changes
  if (!changes.hasMarketChanged && !changes.hasLanguageChanged && changes.hasCurrencyChanged) {
    handleCurrencyOnlyChange(settings, context, current)
    return
  }

  // Handle market changes (highest priority - cross domain)
  if (changes.hasMarketChanged) {
    handleMarketChange(settings, current)
    return
  }

  // Handle language-only changes (same domain)
  if (changes.hasLanguageChanged) {
    handleLanguageOnlyChange(settings, current)
  }
}
